import clsx from 'clsx';
import { unescape } from 'es-toolkit';
import React from 'react';
import toast from 'react-hot-toast';
import ReactHtmlParser from 'react-html-parser';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import linkAnchorIcon from 'images/forum/link_anchor.svg';

import { SecondaryButton } from '@components/Button/variants';
import CommentsContent from '@components/Comments/Content';
import AuthorUnit from '@components/FeedItems/AuthorUnit';
import FlexLink from '@components/FlexLink';
import FollowButton from '@components/FollowButton';
import IconForum from '@components/Images/Rebranding/IconForum';
import MessageUserButton from '@components/MessageUserButton';
import MetaTags from '@components/MetaTags';
import PaywallFull from '@components/Paywall/Standard/Full';
import TimeAgo from '@components/TimeAgo';
import { userName } from '@components/User/userHelpers';
import { useAppContext } from '@contexts/AppContext';
import {
  useCurrentUser,
  useEventTracking,
  useForumPost,
  useForumPostDelete,
  useScrollToAnchor,
} from '@hooks';
import { edit_forum_post_path, forum_path, forum_post_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { CommentUser, CommentableType } from '@utils/backend-api/comments';
import { User } from '@utils/backend-api/currentUser';
import { FollowType } from '@utils/backend-api/follow';
import { FORUM_POSTS_KEY } from '@utils/backend-api/forumPosts';
import { isClosedPoll, isStandalonePollPost } from '@utils/backend-api/polls';
import { LeadTypes } from '@utils/enums';

import { FORUM_BASE_TITLE } from '../ForumLayout';
import ForumPanel from '../ForumPanel';
import ForumPostActions from '../ForumPostActions';
import ForumTags from '../ForumTags';
import PollCard from '../PollCard';
import PollResult from '../PollResult';
import ForumPostScreenshot, { useForumPostScreenshot } from './ForumPostScreenshot';
import styles from './styles.module.scss';

export function forumTitleTag(title) {
  return [title, FORUM_BASE_TITLE].join(' — ');
}

const OG_IMAGE_PATH = '/global/og_images/forum_social_cover.png';

function UserAction({ currentUser, user }: { currentUser: User; user: CommentUser }) {
  return (
    <div className={clsx(styles.actionContainer)}>
      {user.canInteractWithProfile && currentUser?.is_subscriber && (
        <>
          <FollowButton
            followStatus={user.followStatus}
            followeeId={user.identityId}
            followType={FollowType.Identity}
          />
          <MessageUserButton user={user} />
        </>
      )}
    </div>
  );
}

export default function ForumPostPage() {
  const { isWebview } = useAppContext();

  const params = useParams();
  const { forumPost, isForumPostLoading } = useForumPost(params.id);
  const { currentUser } = useCurrentUser();
  const { mutate: deletePost } = useForumPostDelete();
  const isLoading = isForumPostLoading || !forumPost;
  useScrollToAnchor(isLoading);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { trackEvent } = useEventTracking();

  const { onDownloadScreenshot, renderScreenshotContent } = useForumPostScreenshot(
    forumPost?.title
  );

  // TODO: Handle loading error
  if (isLoading) return null;

  const { comment, total_comments_count } = forumPost;
  const { user } = comment;

  const onDeletePost = () => {
    window.confirm('Are you sure you want to delete this post?') &&
      deletePost(forumPost.id, {
        onSuccess: () => {
          trackEvent(click({ clickTarget: ClickTarget.Delete, contentId: forumPost.id }));
          toast.success('Your post has been deleted.');
          queryClient.invalidateQueries(FORUM_POSTS_KEY);
          navigate(forum_path());
          window.scrollTo(0, 0);
        },
        onError: (e) => {
          console.error(e);
          toast.error('Post was not deleted, please try again.');
        },
      });
  };

  const handleEditPostClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.Edit, contentId: forumPost.id }));
  };

  const handleExternalLinkClick = () => {
    trackEvent(
      click({
        clickTarget: ClickTarget.AnchorLink,
        contentId: forumPost.id,
        clickLink: forumPost?.external_url,
      })
    );
  };

  const handleBackToForumClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.BackToForum, contentId: forumPost.id }));
  };

  const description = unescape(forumPost.comment.snippet);
  const body = ReactHtmlParser(comment.body);
  const isPollOnly = isStandalonePollPost(forumPost);
  const isPollClosed = isClosedPoll(forumPost.poll);
  const canVote = !!forumPost.poll && !isPollClosed;

  return (
    <>
      <MetaTags
        title={forumTitleTag(forumPost.title)}
        ogSiteName="The Information Forum"
        ogTitle={forumPost.title}
        twitterTitle={forumPost.title}
        description={description}
        ogImagePath={OG_IMAGE_PATH}
      />
      {currentUser && !!renderScreenshotContent && (
        <ForumPostScreenshot
          title={forumPost.title}
          body={body}
          renderScreenshotContent={renderScreenshotContent}
          length={comment.body.length}
          poll={forumPost.poll}
          isPollOnly={isPollOnly}
        />
      )}

      <div className={styles.forumPostPage}>
        <div className="container-padded container">
          <ForumPanel>
            <div className={styles.forumPostPageHeader}>
              <h1 className={styles.forumPostTitle}>{forumPost.title}</h1>
              {!!currentUser && currentUser.id === user.id && (
                <div className={styles.forumPostPageActions}>
                  <div className={styles.forumPostPageEditActionContainer}>
                    <FlexLink
                      href={edit_forum_post_path(forumPost.id)}
                      className={styles.forumPostPageEditAction}
                      onClickCapture={handleEditPostClick}
                    >
                      Edit
                    </FlexLink>
                  </div>
                  <div className={styles.forumPostPageEditActionContainer}>
                    <button
                      onClick={onDeletePost}
                      className={clsx('unstyled', styles.forumPostPageEditAction)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className={styles.forumPostSection}>
              <div className={styles.subHeader}>
                <AuthorUnit
                  author={{
                    company: user.company_name,
                    isStaff: user?.staff,
                    name: userName(user),
                    picture: user.picture_url,
                    profileEnabled: user.profile_enabled,
                    title: user.title_at_company,
                    username: user.username,
                  }}
                />
                {!forumPost.is_anonymous && (
                  <div className={styles.userActionContainer}>
                    <UserAction user={user} currentUser={currentUser} />
                  </div>
                )}
              </div>
            </div>
            {currentUser?.is_subscriber ? (
              <>
                {!isPollOnly && (
                  <div className={clsx(styles.forumPostBody, styles.forumPostBody, 'break-words')}>
                    {body}
                  </div>
                )}
                {forumPost.poll && (
                  <div className={styles.forumPostSection}>
                    <PollCard
                      poll={forumPost.poll}
                      forumPostId={forumPost.id}
                      showQuestion={!isPollOnly}
                    />
                  </div>
                )}

                <div className={styles.timeStampContainer}>
                  {forumPost.poll && (
                    <div className={styles.forumPostSection}>
                      <PollResult
                        totalVotes={forumPost.poll.total_votes}
                        expiresAt={forumPost.poll.expires_at}
                        pollClosed={isPollClosed}
                        canVote={canVote}
                        userVoted={forumPost.poll.user_voted}
                      />
                    </div>
                  )}
                  <TimeAgo className={styles.forumPostDate} date={comment.created_at} timeFromNow />
                </div>

                {!!forumPost.external_url && (
                  <div className={clsx(styles.forumPostSection, styles.forumPostLinkContainer)}>
                    <img className={styles.linkAnchor} src={linkAnchorIcon} alt="link anchor" />
                    <a
                      href={forumPost.external_url}
                      className={styles.forumPostExternalUrl}
                      onClickCapture={handleExternalLinkClick}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {forumPost.external_url}
                    </a>
                  </div>
                )}
                {forumPost.entity_tags.length > 0 && (
                  <div className={styles.forumPostSection}>
                    <ForumTags tags={forumPost.entity_tags} />
                  </div>
                )}
                {!!comment.edited_at && (
                  <div className={clsx(styles.forumPostSection, styles.forumPostEditedAt)}>
                    Edited <TimeAgo date={comment.edited_at} timeFromNow />
                  </div>
                )}
                <div className={styles.forumPostActions}>
                  <ForumPostActions
                    forumPost={forumPost}
                    onDownloadScreenshot={onDownloadScreenshot}
                  />
                  {!isWebview && (
                    <SecondaryButton
                      as="link"
                      href={forum_path()}
                      size="small"
                      onClickCapture={handleBackToForumClick}
                    >
                      <IconForum className="mr-8 w-16" /> Back to forum
                    </SecondaryButton>
                  )}
                </div>
              </>
            ) : (
              <div className="bg-grayscale-beige sm:bg-grayscale-charcoal">
                <PaywallFull
                  title="Join now to read the full post"
                  leadType={LeadTypes.ForumPost}
                  leadDetails={{ forum_post_title: forumPost.title }}
                  offerLink={forum_post_path(forumPost.id)}
                  skipVerifyNewEmail={false}
                />
              </div>
            )}
          </ForumPanel>
          {currentUser && (
            <ForumPanel id="comments">
              <CommentsContent
                commentableId={forumPost.id}
                commentableType={CommentableType.ForumPost}
                commentsCount={total_comments_count}
                overrideLazyLoad
              />
            </ForumPanel>
          )}
        </div>
      </div>
    </>
  );
}
